import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/api';

interface TenantSettings {
  custodianName?: string;
  custodianAddress?: string;
  defaultRightsPercentage?: number;
  notifications?: {
    documentSigned?: boolean;
    complianceChange?: boolean;
    dailySummary?: boolean;
  };
}

interface Tenant {
  id: string;
  name: string;
  settings?: string;
}

export default function Settings() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Studio settings state
  const [custodianName, setCustodianName] = useState('');
  const [custodianAddress, setCustodianAddress] = useState('');
  const [defaultRightsPercentage, setDefaultRightsPercentage] = useState(50);

  // Notification settings state
  const [documentSigned, setDocumentSigned] = useState(true);
  const [complianceChange, setComplianceChange] = useState(true);
  const [dailySummary, setDailySummary] = useState(false);

  // Password change state
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');

  // Success/error messages
  const [settingsMessage, setSettingsMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [notificationMessage, setNotificationMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Fetch current tenant settings
  const { data: tenantData } = useQuery({
    queryKey: ['tenant-settings'],
    queryFn: () => api.get<{ tenant: Tenant }>('/tenants/current'),
    enabled: user?.role === 'studio_admin' || user?.role === 'super_admin',
  });

  // Initialize form values when data loads
  useEffect(() => {
    if (tenantData?.data.tenant?.settings) {
      try {
        const settings: TenantSettings = JSON.parse(tenantData.data.tenant.settings);
        setCustodianName(settings.custodianName || '');
        setCustodianAddress(settings.custodianAddress || '');
        setDefaultRightsPercentage(settings.defaultRightsPercentage || 50);
        setDocumentSigned(settings.notifications?.documentSigned ?? true);
        setComplianceChange(settings.notifications?.complianceChange ?? true);
        setDailySummary(settings.notifications?.dailySummary ?? false);
      } catch {
        // Settings might not be JSON, ignore
      }
    }
  }, [tenantData]);

  // Save studio settings mutation
  const saveSettingsMutation = useMutation({
    mutationFn: (settings: TenantSettings) =>
      api.put('/tenants/current/settings', { settings }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenant-settings'] });
      setSettingsMessage({ type: 'success', text: 'Settings saved successfully' });
      setTimeout(() => setSettingsMessage(null), 3000);
    },
    onError: () => {
      setSettingsMessage({ type: 'error', text: 'Failed to save settings' });
    },
  });

  // Save notification settings mutation
  const saveNotificationsMutation = useMutation({
    mutationFn: (notifications: TenantSettings['notifications']) =>
      api.put('/tenants/current/settings', {
        settings: { notifications },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenant-settings'] });
      setNotificationMessage({ type: 'success', text: 'Notification preferences saved' });
      setTimeout(() => setNotificationMessage(null), 3000);
    },
    onError: () => {
      setNotificationMessage({ type: 'error', text: 'Failed to save notification preferences' });
    },
  });

  // Change password mutation
  const changePasswordMutation = useMutation({
    mutationFn: (data: { currentPassword: string; newPassword: string }) =>
      api.post('/auth/change-password', data),
    onSuccess: () => {
      setShowPasswordModal(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setPasswordError('');
      alert('Password changed successfully');
    },
    onError: (error: Error) => {
      setPasswordError(error.message || 'Failed to change password');
    },
  });

  const handleSaveSettings = () => {
    saveSettingsMutation.mutate({
      custodianName,
      custodianAddress,
      defaultRightsPercentage,
    });
  };

  const handleSaveNotifications = () => {
    saveNotificationsMutation.mutate({
      documentSigned,
      complianceChange,
      dailySummary,
    });
  };

  const handleChangePassword = () => {
    setPasswordError('');

    if (newPassword.length < 8) {
      setPasswordError('New password must be at least 8 characters');
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError('Passwords do not match');
      return;
    }

    changePasswordMutation.mutate({
      currentPassword,
      newPassword,
    });
  };

  return (
    <div className="page">
      <div className="page-header">
        <h1>Settings</h1>
      </div>

      <div className="card">
        <h3>Account Settings</h3>
        <dl className="detail-list">
          <dt>Email</dt>
          <dd>{user?.email}</dd>

          <dt>Role</dt>
          <dd>{user?.role}</dd>
        </dl>

        <div className="form-actions">
          <button
            className="btn btn-secondary"
            onClick={() => setShowPasswordModal(true)}
          >
            Change Password
          </button>
        </div>
      </div>

      {(user?.role === 'studio_admin' || user?.role === 'super_admin') && (
        <div className="card">
          <h3>Studio Settings</h3>
          <p className="text-muted">
            Configure your studio's preferences and compliance settings.
          </p>

          {settingsMessage && (
            <div className={`message message-${settingsMessage.type}`}>
              {settingsMessage.text}
            </div>
          )}

          <div className="settings-section">
            <h4>Custodian of Records</h4>
            <div className="form-group">
              <label>Custodian Name</label>
              <input
                type="text"
                placeholder="Enter custodian name"
                value={custodianName}
                onChange={(e) => setCustodianName(e.target.value)}
              />
            </div>
            <div className="form-group">
              <label>Custodian Address</label>
              <textarea
                placeholder="Enter custodian address"
                rows={3}
                value={custodianAddress}
                onChange={(e) => setCustodianAddress(e.target.value)}
              />
            </div>
          </div>

          <div className="settings-section">
            <h4>Default Document Settings</h4>
            <div className="form-group">
              <label>Default Rights Percentage</label>
              <input
                type="number"
                min="0"
                max="100"
                placeholder="50"
                value={defaultRightsPercentage}
                onChange={(e) => setDefaultRightsPercentage(parseInt(e.target.value) || 0)}
              />
            </div>
          </div>

          <div className="form-actions">
            <button
              className="btn btn-primary"
              onClick={handleSaveSettings}
              disabled={saveSettingsMutation.isPending}
            >
              {saveSettingsMutation.isPending ? 'Saving...' : 'Save Settings'}
            </button>
          </div>
        </div>
      )}

      <div className="card">
        <h3>Notifications</h3>

        {notificationMessage && (
          <div className={`message message-${notificationMessage.type}`}>
            {notificationMessage.text}
          </div>
        )}

        <div className="settings-section">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={documentSigned}
              onChange={(e) => setDocumentSigned(e.target.checked)}
            />
            Email me when documents are signed
          </label>
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={complianceChange}
              onChange={(e) => setComplianceChange(e.target.checked)}
            />
            Email me when compliance status changes
          </label>
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={dailySummary}
              onChange={(e) => setDailySummary(e.target.checked)}
            />
            Daily compliance summary
          </label>
        </div>

        <div className="form-actions">
          <button
            className="btn btn-primary"
            onClick={handleSaveNotifications}
            disabled={saveNotificationsMutation.isPending}
          >
            {saveNotificationsMutation.isPending ? 'Saving...' : 'Save Notifications'}
          </button>
        </div>
      </div>

      {/* Password Change Modal */}
      {showPasswordModal && (
        <div className="modal-overlay" onClick={() => setShowPasswordModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Change Password</h2>
              <button
                className="modal-close"
                onClick={() => setShowPasswordModal(false)}
              >
                &times;
              </button>
            </div>
            <div className="modal-body">
              {passwordError && (
                <div className="error-message">{passwordError}</div>
              )}

              <div className="form-group">
                <label>Current Password</label>
                <input
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder="Enter current password"
                />
              </div>

              <div className="form-group">
                <label>New Password</label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password (min 8 characters)"
                />
              </div>

              <div className="form-group">
                <label>Confirm New Password</label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                />
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => setShowPasswordModal(false)}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleChangePassword}
                disabled={changePasswordMutation.isPending}
              >
                {changePasswordMutation.isPending ? 'Changing...' : 'Change Password'}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .settings-section {
          margin-bottom: 24px;
        }

        .settings-section h4 {
          margin-bottom: 12px;
          color: #374151;
        }

        .checkbox-label {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 12px;
          cursor: pointer;
        }

        .checkbox-label input[type="checkbox"] {
          width: 18px;
          height: 18px;
        }

        .message {
          padding: 12px;
          border-radius: 6px;
          margin-bottom: 16px;
        }

        .message-success {
          background: #d1fae5;
          color: #065f46;
        }

        .message-error {
          background: #fee2e2;
          color: #991b1b;
        }

        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
        }

        .modal {
          background: white;
          border-radius: 12px;
          width: 100%;
          max-width: 400px;
        }

        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 20px;
          border-bottom: 1px solid #e5e7eb;
        }

        .modal-header h2 {
          margin: 0;
          font-size: 1.25rem;
        }

        .modal-close {
          background: none;
          border: none;
          font-size: 1.5rem;
          cursor: pointer;
          color: #6b7280;
        }

        .modal-body {
          padding: 20px;
        }

        .modal-footer {
          display: flex;
          justify-content: flex-end;
          gap: 12px;
          padding: 20px;
          border-top: 1px solid #e5e7eb;
        }

        .error-message {
          background: #fee2e2;
          color: #991b1b;
          padding: 12px;
          border-radius: 6px;
          margin-bottom: 16px;
        }
      `}</style>
    </div>
  );
}
